import type { DateInputValue, DashboardUpcomingItem } from "../types/dashboard.types";

export const safeLocale = (locale?: string | null): string => {
  if (!locale || typeof locale !== "string") return "en";
  try {
    Intl.DateTimeFormat.supportedLocalesOf([locale]);
    return locale;
  } catch {
    return "en";
  }
};

export const toDateValue = (value: DateInputValue): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value.map(Number);
    const date = new Date(year, month - 1, day, hour, minute, second);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const millis = value < 1_000_000_000_000 ? value * 1000 : value;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+(?:\.\d+)?$/.test(trimmed)) return toDateValue(Number(trimmed));
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

export const toDateMillis = (value: DateInputValue): number => toDateValue(value)?.getTime() ?? Number.NaN;

export const isSameCalendarDay = (a: DateInputValue, b: DateInputValue): boolean => {
  const da = toDateValue(a);
  const db = toDateValue(b);
  return Boolean(
    da &&
      db &&
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate(),
  );
};

export const isAllDayRange = (startValue: DateInputValue, endValue: DateInputValue): boolean => {
  const start = toDateValue(startValue);
  const end = toDateValue(endValue);
  if (!start) return false;
  const startIsMidnight = start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0;
  if (!end) return startIsMidnight;
  return startIsMidnight && end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0;
};

export const toDayKey = (millis: number): string => {
  if (!Number.isFinite(millis)) return "";
  const date = new Date(millis);
  const pad2 = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

export const formatDateLong = (value: DateInputValue, locale?: string | null): string => {
  const date = toDateValue(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat(safeLocale(locale), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

export const formatTimeOnly = (value: DateInputValue, locale?: string | null): string => {
  const date = toDateValue(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(safeLocale(locale), { hour: "2-digit", minute: "2-digit" }).format(date);
};

export const formatCompactDateTime = (value: DateInputValue, locale?: string | null): string => {
  const date = toDateValue(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat(safeLocale(locale), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const formatUpcomingMeta = (item: DashboardUpcomingItem | null, locale?: string | null, allDayLabel = "All day"): string => {
  if (!item) return "-";
  const start = toDateValue(item.date);
  const end = toDateValue(item.endDate);
  const allDay = Boolean(item.allDay) || isAllDayRange(item.date, item.endDate);
  if (!start) return item.type || "-";
  const l = safeLocale(locale);
  const dateLabel = new Intl.DateTimeFormat(l, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(start);
  if (allDay) {
    if (end && !isSameCalendarDay(start, end)) {
      const endLabel = new Intl.DateTimeFormat(l, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(end);
      return `${dateLabel} - ${endLabel} · ${item.type}`;
    }
    return `${dateLabel} · ${allDayLabel} · ${item.type}`;
  }
  const startTime = formatTimeOnly(start, l);
  const endTime = end ? formatTimeOnly(end, l) : "";
  return `${dateLabel} · ${startTime}${endTime ? ` - ${endTime}` : ""} · ${item.type}`;
};

export const formatDetailDate = (item: DashboardUpcomingItem | null, locale?: string | null): string => {
  if (!item?.date) return "-";
  const start = toDateValue(item.date);
  const end = toDateValue(item.endDate);
  if (!start) return "-";
  if ((Boolean(item.allDay) || isAllDayRange(item.date, item.endDate)) && end && !isSameCalendarDay(start, end)) {
    return `${formatDateLong(start, locale)} - ${formatDateLong(end, locale)}`;
  }
  return formatDateLong(start, locale);
};

export const formatDetailTime = (item: DashboardUpcomingItem | null, locale?: string | null, allDayLabel = "All day"): string => {
  if (!item?.date) return "-";
  if (Boolean(item.allDay) || isAllDayRange(item.date, item.endDate)) return allDayLabel;
  const start = toDateValue(item.date);
  const end = toDateValue(item.endDate);
  if (!start) return "-";
  const duration = end && end.getTime() > start.getTime() ? Math.round((end.getTime() - start.getTime()) / 60000) : null;
  const durationLabel = duration == null ? "" : duration >= 60 && duration % 60 === 0 ? ` (${duration / 60} h)` : ` (${duration} min)`;
  return `${formatTimeOnly(start, locale)}${end ? ` - ${formatTimeOnly(end, locale)}` : ""}${durationLabel}`;
};
